(function() {
    if (customElements.get("base-slideshow") === undefined) {

        function toggleTabIndex(disable, enable) {
            if (typeof window.pixel3A11y?.toggleTabIndex === "function") {
                window.pixel3A11y.toggleTabIndex(disable, enable);
            }
        }

        const template = document.createElement('template');
        template.innerHTML = `
        <style>
        :host {
            --slideshow-transition-speed: .5s;
            --slideshow-transition-timing-function: ease;
            --slideshow-control-size: 48px;
            --slideshow-control-color: white;
            --slideshow-transition-duration: .5s;
            --slideshow-height-transition-duration: .25s;
            --slideshow-height-transition-timing-function: ease;
            /* will be set by js */
            --slideshow-active-index: 0;
            --slideshow-outer-width: 100vw;
            --slideshow-active-height: 56.25%;
        
            display: block;
            position: relative;
        }
        :host([transition-duration="slow"]) {
            --slideshow-transition-duration: 1s;
        }
        :host([transition-duration="fast"]) {
            --slideshow-transition-duration: .25s;
        }
        [part="outer-hull"] {
            width: 100%;
            overflow: hidden;
            position: relative;
            z-index: 1;
            transition: height var(--slideshow-height-transition-duration) var(--slideshow-height-transition-timing-function);
            height: var(--slideshow-active-height);
        }
        [part="inner-hull"] ::slotted(*) {
            display: block;
            flex-basis: var(--slideshow-outer-width);
            width: var(--slideshow-outer-width);
        }
        .no-transition {
            transition: none!important;
        }
        
        /* animation carousel */
        :host(:not([animation])) [part="inner-hull"],
        :host([animation="carousel"]) [part="inner-hull"] {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            justify-items: flex-start;
            will-change: transform;
            transform: translateX(calc(var(--slideshow-outer-width) * var(--slideshow-active-index) * -1));
            width: 999999px;
            transition: transform var(--slideshow-transition-duration) var(--slideshow-transition-timing-function);
        }
        
        /* animation crossfade */
        :host([animation="crossfade"]) [part="inner-hull"] ::slotted(*) {
            transition: opacity var(--slideshow-transition-duration) var(--slideshow-transition-timing-function);
            position: absolute;
            left: 0;
            top: 0;
            width: var(--slideshow-outer-width);
            height: auto;
            z-index: 0;
            opacity: 0;
        }
        :host([animation="crossfade"]) [part="inner-hull"] ::slotted(.last-active) {
            z-index: 1;
        }
        :host([animation="crossfade"]) [part="inner-hull"] ::slotted(.now-active) {
            z-index: 2;
            opacity: 1;
        }
        
        /* controls */
        button {
            appearance: none;
            border: none;
            background: transparent;
            display: none;
            color: var(--slideshow-control-color);
            position: absolute;
            bottom: 0;
            pointer-events: all;
        }
        [part="controls"] {
            position: absolute;
            z-index: 2;
            pointer-events: none;
            left: 0;
            right: 0;
            margin: 0;
            padding: 0;
            bottom: 0;
            color: var(--slideshow-control-color);
            align-items: center;
            justify-content: space-between;
            min-height: var(--slideshow-control-size);
        }
        :host([controls-position="middle"]) [part="controls"] {
            bottom: calc(50% - var(--slideshow-control-size) * 0.5);
        }
        :host([controls-position="top"]) [part="controls"] {
            bottom: 100%;
            transform: translateY(100%);
        }
        
        :is([part="default-prev-icon"],[part="default-next-icon"]) {
            /* display: flex will be set of control is enabled */
            font-family: 'Arial', sans-serif;
            align-items: center;
            justify-content: center;
            width: var(--slideshow-control-size);
            height: var(--slideshow-control-size);
            font-size: var(--slideshow-control-size);
            line-height: 1;
        }
        /* prev-button */
        :host([controls*="prev"]) [part="control-prev"] {
            display: flex;
        }
        [part="control-prev"] {
            left: 0;
        }
        [part="default-prev-icon"]::before {
            content: "‹";
        }
        /* next-button */
        :host([controls*="next"]) [part="control-next"] {
            display: flex;
        }
        [part="control-next"] {
            right: 0;
        }
        [part="default-next-icon"]::before {
            content: "›";
        }
        </style>
        <div part="outer-hull">
            <div part="inner-hull">
                <slot name="internal-before"></slot>
                <slot></slot>
                <slot name="internal-after"></slot>
            </div>
        </div>
        <menu part="controls">
            <button part="control-prev" aria-label="Previous slide"> 
                <slot name="prev-icon"><span part="default-prev-icon" title="Previous slide"></span></slot>
            </button>
            <button part="control-next" aria-label="Next slide">
                <slot name="next-icon"><span part="default-next-icon" title="Next slide"></span></slot>
            </button>
        </menu>
        `;
        customElements.define("base-slideshow", class extends HTMLElement {

            static get observedAttributes() {
                return [
                    'transition-duration',
                    'autoplay',
                ];
            }

            /**
             * get interval speed from attribute, support integer values and keywords from predefined speeds (slow, medium, fast)
             */
            get intervalSpeed() {
                const intervalSpeeds = {
                    "slow": 10000,
                    "medium": 5000,
                    "fast": 2500,
                };
                if (this.getAttribute('interval-speed')) {
                    const speed = this.getAttribute('interval-speed');
                    if (typeof speed === "number" && speed > 0) {
                        return speed;
                    } else if (typeof speed === "string") {
                        if (intervalSpeeds[speed] !== undefined) {
                            return intervalSpeeds[speed];
                        } else if(/^\d+$/.test(speed)) {
                            return parseInt(speed);
                        }
                    }
                }
                return intervalSpeeds.medium;
            }
            /**
             * we support transition-speeds from predefined values (css-vars) and by providing a valid string like .7s, 500ms or 3s
             */
            set transitionDuration(value) {
                if (!value) {
                    this.style.removeProperty('--slideshow-transition-duration');
                } else {
                    this.style.setProperty('--slideshow-transition-duration', value);
                }
            }
            get autoplay() {
                if (!this.hasAttribute('autoplay')) {
                    return false;
                }
                const attrValue = this.getAttribute('autoplay').toLowerCase();
                return attrValue !== '0' && attrValue !== 'false';
            }
            get animation() {
                return this.getAttribute('animation') || 'carousel';
            }
            set autoplay(value) {
                if (typeof value === "string") {
                    value = value.toLowerCase();
                    value = value !== "0" && value !== "false";
                }
                if (value) {
                    this.play();
                } else {
                    this.pause();
                }
            }
            constructor() {
                super();
                this.attachShadow({mode: "open"}).appendChild(template.content.cloneNode(true));
                this.interval = 0;
                this.activeItem = null;

                this.visibilityObserver = null;
                this.visibleItems = new Set();
            }

            connectedCallback() {
                this.carouselTransitionEndCallback = () => {
                    const items = this.items;
                    if (this.animation === 'carousel' && items.length > 1) {
                        const currentActiveCssPropertyValue = parseInt(getComputedStyle(this).getPropertyValue('--slideshow-active-index'));
                        if (currentActiveCssPropertyValue === 0) {
                            // virtual last element, so lets got to the real last element
                            this.disableTransition();
                            this.goto(items.length - 1);
                            this.enableTransition();
                        } else if (currentActiveCssPropertyValue === items.length + 1) {
                            // virtual first element, so goto the real first element
                            this.disableTransition();
                            this.goto(0);
                            this.enableTransition();
                        }
                    }
                };
                this.shadowRoot.querySelector('[part="inner-hull"]').addEventListener('transitionend', this.carouselTransitionEndCallback, {capture: true});
                this.shadowRoot.querySelector('[part="inner-hull"]').addEventListener('transitionend', () => {
                    this.dispatchEvent(new CustomEvent('slideshow.slidechanged', {cancelable: false}));
                });

                // add swipe support
                let distX, distY, startX, startY, tolerance = 100;

                this.addEventListener('touchstart', e => {
                    startX = e.changedTouches[0].pageX;
                    startY = e.changedTouches[0].pageY;
                }, {
                    passive: true
                });
                this.addEventListener('touchend', e => {
                    distX = e.changedTouches[0].pageX - startX // get horizontal dist traveled by finger while in contact with surface
                    distY = e.changedTouches[0].pageY - startY // get vertical dist traveled by finger while in contact with surface

                    if (distY < tolerance && distY > -tolerance && distX < -tolerance){
                        this.dispatchEvent(new CustomEvent('slideshow.swiperight', {cancelable: false}));

                    } else if (distY < tolerance && distY > -tolerance && distX > tolerance){
                        this.dispatchEvent(new CustomEvent('slideshow.swipeleft', {cancelable: false}));
                    }
                }, {
                    passive: true,
                });
                this.addEventListener('slideshow.swiperight', () => this.next(), {
                    passive: true
                });
                this.addEventListener('slideshow.swipeleft', () => this.prev(), {
                    passive: true
                });
                this.shadowRoot.querySelector('[part="control-prev"]').addEventListener('click', () => this.prev());
                this.shadowRoot.querySelector('[part="control-next"]').addEventListener('click', () => this.next());
                const setSize = (newWidth) => {
                    const contentWidthValue = `${Math.round(newWidth)}px`;
                    const currentValue = getComputedStyle(this).getPropertyValue('--slideshow-outer-width');
                    if (contentWidthValue !== currentValue) {
                        this.style.setProperty('--slideshow-outer-width', contentWidthValue);
                        this.offsetHeight; // force re-paint
                    }
                    const activeItem = this.getItemIndex();
                    this.disableTransition();
                    this.goto(activeItem);
                    this.enableTransition();
                };
                setSize(this.offsetWidth);
                let resize;
                this.resizeObserver = new ResizeObserver((entries) => {
                    clearTimeout(resize);
                    resize = setTimeout(() => {
                        const contentWidth = entries[0].contentRect.width;
                        setSize(contentWidth);
                    }, 100);
                });
                this.resizeObserver.observed = [];
                this.resizeObserver.observe(this);
                let initTimeout = 0;
                this.init = () => {
                    clearTimeout(initTimeout);
                    initTimeout = setTimeout(() => {
                        if (this.autoplay) {
                            this.play();
                        }
                        this.disableTransition();
                        this.addInternals();
                        this.goto(0);
                        this.enableTransition();
                        this.addSingleSlideEvents();
                        // we need to observe the items too because they may change due to async loading...
                        // already tried to remove this but it resulted in a buggy behaviour

                        if (!(this.parentElement.classList.contains('base-simple-slider-wrapper--multiple-items')) && this.animation !== 'carousel') {
                            // but this leads to a buggy behavoir for multiple items @see https://ticket.pixelpoint.at/attachments/43014
                            // so this is only done for single items
                            this.items.forEach(item => {
                                if (this.resizeObserver.observed.indexOf(item) === -1) {
                                    this.resizeObserver.observed.push(item);
                                    this.resizeObserver.observe(item);
                                }
                            });
                        }

                        if (this.parentElement.classList.contains('base-simple-slider-wrapper--multiple-items') && this.animation === 'carousel' && this.items.length > 1) { 
                            this.initVisibilityObserver();
                        }
                    }, 20);
                };
                this.init();
                this.addEventListener('slotchange', e => {
                    if (e.name === '') {
                        this.init();
                    }
                });
            }

            // Initialize viewport visibility observer for carousel items
            initVisibilityObserver() {
                if (this.visibilityObserver) {
                    this.visibilityObserver.disconnect();
                }
                this.visibilityObserver = new IntersectionObserver((entries) => {
                    entries.forEach(entry => {
                        const item = entry.target;
                        if (entry.isIntersecting) {
                            this.visibleItems.add(item);
                            item.classList.add('in-viewport');
                            this.dispatchEvent(new CustomEvent('slideshow.itemvisible', {
                                detail: { item, isVisible: true },
                                cancelable: false
                            }));
                        } else {
                            this.visibleItems.delete(item);
                            item.classList.remove('in-viewport');
                            this.dispatchEvent(new CustomEvent('slideshow.itemhidden', {
                                detail: { item, isVisible: false },
                                cancelable: false
                            }));
                        }
                    });
                }, {
                    root: this.parentElement, // .base-simple-slider-wrapper
                    rootMargin: '0px',
                    threshold: [0.9] // slide item must be to 90% visible
                });
                // Observe all current items
                this.items.forEach(item => {
                    this.visibilityObserver.observe(item);
                });
            }

            get itemsSlot() {
                return this.shadowRoot.querySelector('slot:not([name])');
            }

            get items() {
                return this.itemsSlot.assignedElements();
            }

            get numOfInternals() {
                if (this.hasAttribute('internals')) {
                    const rawValue = parseInt(this.getAttribute('internals'));
                    return Math.min(20, Math.max(0, rawValue));
                }
                return 1;
            }

            addInternals() {
                this.removeInternals();
                if (this.animation !== "carousel") {
                    return;
                }
                const items = this.items;
                if (items.length < 1) {
                    return;
                }
                if (items.length === 1 && this.numOfInternals === 1) {
                    // this is necessary for us to NOT break things
                    return;
                }
                const disable = [];
                for (let i = 1; i <= this.numOfInternals; i++) {
                    const cloneIndexFromBegin = (i-1) % items.length;
                    const cloneIndexFromBehind = (items.length - 1) - cloneIndexFromBegin;
                    const before = items[cloneIndexFromBehind].cloneNode(true);
                    disable.push(before);
                    before.classList.remove('last-active', 'now-active', 'next-active');
                    before.setAttribute('slot', 'internal-before');
                    this.insertAdjacentElement('afterbegin', before);
                    const after = items[cloneIndexFromBegin].cloneNode(true);
                    disable.push(after);
                    after.classList.remove('last-active', 'now-active', 'next-active');
                    after.setAttribute('slot', 'internal-after');
                    this.insertAdjacentElement('beforeend', after);
                }
                toggleTabIndex(disable);
            }

            removeInternals() {
                const before = this.querySelector(':scope > *[slot="internal-before"]');
                if (before) {
                    this.removeChild(before);
                }
                const after = this.querySelector(':scope > *[slot="internal-after"]');
                if (after) {
                    this.removeChild(after);
                }
            }

            attributeChangedCallback(name, oldVal, newVal) {
                if (oldVal !== newVal) {
                    if (name === 'animation') {
                        if (oldVal === 'carousel') {
                            this.removeInternals();
                        } else if (newVal === 'carousel') {
                            this.addInternals();
                        }
                    } else {
                        this[name] = newVal;
                    }
                }
            }
            addSingleSlideEvents() {
                this.items.forEach(item => {
                    if (!('transitionendEventAdded' in item)) {
                        item['transitionendEventAdded'] = true;
                        item.addEventListener('transitionend', () => {
                            this.dispatchEvent(new CustomEvent('slideshow.slidechanged', {cancelable: false}));
                        });
                    }
                })
            }

            play() {
                if (!this.hasAttribute('playing')) {
                    this.setAttribute('playing', 'playing');
                    this.dispatchEvent(new CustomEvent('slideshow.play', {cancelable: false}));
                }
                clearInterval(this.interval);
                this.interval = setInterval(() => {
                    const event = new CustomEvent('slideshow.autoplay', {
                        cancelable: true,
                        bubbles: true
                    });
                    this.dispatchEvent(event);
                    if (!event.defaultPrevented) {
                        const cl = document.documentElement.classList;
                        if (cl.contains('using-keyboard') || cl.contains('gpu-popup-open')) {
                            return;
                        }
                        this.next();
                    }
                }, this.intervalSpeed);
            }
            pause() {
                this.removeAttribute('playing');
                clearInterval(this.interval);
                this.dispatchEvent(new CustomEvent('slideshow.pause', {cancelable: false}));
            }
            next() {
                // determine next element and call goto with index
                this.goto('next');
                // in case the slideshow is playing and the user initiates a slide-change, we might end up with
                // 2 slide changes - to prevent this, we make sure to cancel the interval, so the next change happens
                // in exactly this.intervalSpeed
                if (this.getAttribute('playing') === 'playing') {
                    this.play();
                }
            }
            prev() {
                // determine previous element and call goto with index
                this.goto('prev');
                // @see next()
                if (this.getAttribute('playing') === 'playing') {
                    this.play();
                }
            }
            goto(command) {
                let index = command;
                if (command === 'next') {
                    index = this.getItemIndex(1);
                } else if (command === 'prev') {
                    index = this.getItemIndex(-1);
                }
                // get current valid items array
                const items = this.items;
                // get item that is active now and mark it as last-active
                const lastActive = this.getItemIndex();
                // nowActive will be the desired index
                const nowActive = index;
                // set activeItem so, getting the activeItem + 1 will return the correct value
                this.activeItem = items[nowActive];
                // now we use the incremented nowActive index to determine the next element
                const nextActive = this.getItemIndex(1);
                const activeHeight = this.activeItem?.scrollHeight ?? 0;
                this.style.setProperty('--slideshow-active-height', `${activeHeight}px`);
                this.offsetHeight; // force re-paint
                // to make slide-animations possible, we store the active index as css-var
                let cssActiveItem = nowActive;
                if (this.animation === 'carousel' && items.length > 1) {
                    // we are dealing with virtual elements
                    if (command === "next" && lastActive === items.length - 1) {
                        // currently active (lastActive) is the last element and we want to goto the next one
                        // so we goto our virtual after element this time
                        // the transitionend listener (constructor) will afterwards set the real "0" item as active, so we start at the beginning again
                        cssActiveItem = items.length + 1;
                    } else if (command === "prev" && lastActive === 0) {
                        // the other way around: currently active is the fist element and we want to the prev one
                        // so we goto our virtual before element this time
                        cssActiveItem = 0;
                    } else {
                        // by default we simply need to go once further than intended because of the prepended before item
                        cssActiveItem += 1;
                    }
                }
                this.dispatchEvent(new CustomEvent('slideshow.slidechange', {
                    cancelable: false,
                    detail: {
                        active: this.activeItem,
                        activeIndex: nowActive
                    }
                }));
                const enableTabIndex = [];
                const disableTabIndex = [];
                // adjust classes of all items
                for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
                    const classes = items[itemIndex].classList;
                    if (itemIndex === nowActive) {
                        classes.add('now-active');
                        enableTabIndex.push(items[itemIndex]);
                    } else {
                        classes.remove('now-active');
                        disableTabIndex.push(items[itemIndex]);
                    }
                    if (itemIndex === lastActive) {
                        classes.add('last-active');
                    } else {
                        classes.remove('last-active');
                    }
                    if (itemIndex === nextActive) {
                        classes.add('next-active');
                    } else {
                        classes.remove('next-active');
                    }

                    // if it is ah carousel and multiple items are visible in the wrapper - all visible items should be focusable
                    if (this.querySelectorAll(".in-viewport").length > 1) { 
                        if(classes.contains("in-viewport") && !classes.contains("now-active")) {
                          enableTabIndex.push(items[itemIndex]);
                          // remove the now enableTabIndex item from the disableTabIndex array
                          const indexInDisable = disableTabIndex.indexOf(items[itemIndex]);
                          if (indexInDisable > -1) {
                            disableTabIndex.splice(indexInDisable, 1);
                          }
                        }
                    }
                }

                toggleTabIndex(disableTabIndex, enableTabIndex);

                this.style.setProperty('--slideshow-active-index', cssActiveItem);
                // wee need to call this to force css changes, otherwise disabling the transition wont work...
                // @see https://stackoverflow.com/questions/11131875/what-is-the-cleanest-way-to-disable-css-transition-effects-temporarily
                this.offsetHeight;
            }
            disableTransition() {
                this.shadowRoot.querySelector('[part="inner-hull"]').classList.add('no-transition');
                this.shadowRoot.querySelector('[part="outer-hull"]').classList.add('no-transition');
                this.offsetHeight;
            }
            enableTransition() {
                this.shadowRoot.querySelector('[part="inner-hull"]').classList.remove('no-transition');
                this.shadowRoot.querySelector('[part="outer-hull"]').classList.remove('no-transition');
                this.offsetHeight;
            }
            getItemIndex(modify = 0) {
                const items = this.items;
                let currentIndex = 0;
                if (this.activeItem) {
                    currentIndex = items.findIndex(candidate => candidate === this.activeItem);
                }
                if (modify !== 0 && items.length > 0) {
                    const length = items.length;
                    currentIndex = currentIndex + modify;
                    while (currentIndex < 0) {
                        currentIndex = currentIndex + length;
                    }
                    while (currentIndex >= length) {
                        currentIndex = currentIndex - length;
                    }
                }
                return currentIndex;
            }
        });
    }
})();
